// OnboardingChecklist — shown on the manager dashboard for new companies
// until all setup steps are complete. Reduces drop-off in the first week.
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, MapPin, Users, Building2, CalendarPlus, Send, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";

const HIDE_KEY = "rostr.onboarding_checklist_dismissed";

export default function OnboardingChecklist({ stats }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(HIDE_KEY) === "1"; } catch { return false; }
  });

  const steps = [
    ...(isOwner ? [{ key: "location", labelKey: "ocl_step_location", to: "/locations", icon: MapPin, done: (stats?.locations || 0) > 0 }] : []),
    { key: "team", labelKey: "ocl_step_team", to: "/teams", icon: Building2, done: (stats?.teams || 0) > 0 },
    { key: "employees", labelKey: "ocl_step_employees", to: "/employees", icon: Users, done: (stats?.employees || 0) > 1 },
    { key: "shift", labelKey: "ocl_step_shift", to: "/roster", icon: CalendarPlus, done: (stats?.shifts_this_week || 0) > 0 },
    { key: "publish", labelKey: "ocl_step_publish", to: "/roster", icon: Send, done: (stats?.published || 0) > 0 },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  useEffect(() => {
    if (allDone) {
      // Auto-hide after all steps complete (persist so it doesn't reappear).
      try { localStorage.setItem(HIDE_KEY, "1"); } catch {}
    }
  }, [allDone]);

  if (dismissed || allDone) return null;

  const dismiss = () => { try { localStorage.setItem(HIDE_KEY, "1"); } catch {}; setDismissed(true); };

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      className="card p-6 md:p-7 mb-6 relative overflow-hidden"
      data-testid="onboarding-checklist"
      style={{
        background: "linear-gradient(135deg, hsl(var(--surface)) 0%, hsl(var(--primary-soft)) 100%)",
        borderColor: "hsl(var(--primary) / 0.25)",
      }}
    >
      {/* Decorative gradient blob */}
      <div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        style={{ top: -60, right: -60, width: 220, height: 220, background: "radial-gradient(circle, hsl(var(--primary) / 0.18) 0%, transparent 70%)", filter: "blur(30px)" }}
      />
      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="eyebrow flex items-center gap-1.5">
            <Check size={11} strokeWidth={2.4} /> {t("ocl_eyebrow")}
          </div>
          <h2 className="font-display text-[20px] md:text-[22px] font-bold tracking-tight text-[hsl(var(--text-strong))] mt-1 leading-tight">
            {t("ocl_title")}
          </h2>
          <div className="text-[13px] text-[hsl(var(--text-muted))] mt-1">
            {t("ocl_progress").replace("{done}", doneCount).replace("{total}", steps.length)}
          </div>
        </div>
        <button
          onClick={dismiss}
          data-testid="onboarding-checklist-dismiss"
          className="text-[11px] font-semibold text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-strong))] whitespace-nowrap shrink-0"
        >
          {t("ocl_dismiss")}
        </button>
      </div>

      {/* Progress bar */}
      <div className="relative h-1.5 rounded-full bg-[hsl(var(--border))] mt-4 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${(doneCount / steps.length) * 100}%` }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ background: "linear-gradient(90deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)" }}
        />
      </div>

      {/* Steps */}
      <div className="relative mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <Link
              key={s.key}
              to={s.to}
              data-testid={`ocl-step-${s.key}`}
              className={`group flex items-center gap-3 rounded-2xl border p-3 transition-colors
                ${s.done
                  ? "bg-[hsl(var(--success-soft, 145_60%_94%))] border-[hsl(var(--success, 145_60%_45%))/0.35]"
                  : "bg-white/70 hairline hover:border-[hsl(var(--primary))]"}
              `}
            >
              <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${s.done ? "bg-[hsl(var(--success, 145_60%_45%))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--text-strong))]"}`}>
                {s.done ? <Check size={16} strokeWidth={2.6} /> : <Icon size={15} strokeWidth={2} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-[12.5px] font-bold leading-tight ${s.done ? "text-[hsl(var(--text-muted))] line-through" : "text-[hsl(var(--text-strong))]"}`}>
                  {t(s.labelKey)}
                </div>
              </div>
              {!s.done && <ArrowRight size={13} className="text-[hsl(var(--text-muted))] group-hover:text-[hsl(var(--primary))] shrink-0" />}
            </Link>
          );
        })}
      </div>
    </motion.section>
  );
}
